import api from './api';
import { socketService } from '@/common/socket/SocketService';
import { installVueSocketIo } from '@/common/socket/installVue3SocketIO';
import router from '../../router/index';
import { getDeviceInfo, getUserInfo } from '../../utils/deviceInfo.js';
import metricsService from '../../services/metricsService.js';
import { useSocketStore } from '../../rennovation/planning-view/store/bindSocketToPinia';

let socketStore;
function getSocketStore() {
	if (!socketStore) {
		socketStore = useSocketStore();
	}
	return socketStore;
}

const state = {
	user: null,
	status: '',
	token: null,
	error: false,
	success: false,
	message: '',
	resetToken: false,
	tenants: [],
	signupAllowed: null,
	selectedTenant: null,
};

const getters = {
	isLoggedIn: (state) => {
		return state.user !== null;
	},
	checkPermission: (state) => (permissionName) => {
		return !!state.user.permissions?.[permissionName];
	},
	isSuperUser() {
		return state.user ? !!state.user.superuser : false;
	},
};

const mutations = {
	auth_success(state, { user }) {
		state.user = user;
		state.status = 'success';
		state.error = false;
	},
	set_token(state, { token }) {
		state.token = token;
	},
	resetToken(state) {
		state.resetToken = true;
		state.error = false;
	},
	noResetToken(state) {
		state.resetToken = false;
	},
	auth_error(state, { message }) {
		state.status = 'error';
		state.error = true;
		state.message = message || '';
		state.user = null;
	},
	auth_removeErrorMessage(state) {
		state.error = false;
		state.message = '';
	},
	auth_removeSuccessMessage(state) {
		state.success = false;
		state.message = '';
	},
	auth_successRequest(state, { message }) {
		state.success = true;
		state.error = false;
		state.message = message;
	},
	auth_successReset(state, { message }) {
		state.success = true;
		state.error = false;
		state.message = message;
	},
	logout(state) {
		state.status = '';
		state.token = null;
		state.user = null;
	},
	set_tenants(state, { tenants }) {
		state.tenants = tenants.map((tenant) => {
			return { value: tenant.name, text: tenant.title };
		});
		if (state.tenants.length) {
			state.selectedTenant = state.tenants[0].value;
		}
	},
	set_signupAllowed(state, signupAllowed) {
		state.signupAllowed = signupAllowed;
	},
	set_selectedTenant(state, tenant) {
		state.selectedTenant = tenant;
	},
	set_sortOrder(state, order) {
		state.user.userGroupSortOrder = order;
	},
};

const actions = {
	login({ state, commit, rootGetters, dispatch }, { username, password, tenant }) {
		return api
			.login(username, password, tenant)
			.then(async (response) => {
				const token = response.data.token;
				commit('set_token', { token });
				await dispatch('load');
			})
			.catch((err) => {
				const message = err.response.data.error.message;
				commit('auth_error', { message });
			});
	},

	async logout({ commit, dispatch }) {
		await dispatch('webPush/unregisterDevice', null, { root: true });
		await metricsService.endSession();
		await api.logout();
		commit('logout');
		await dispatch('webPush/stopSubscriptionTimer', null, { root: true });
		console.log('Successfully logged out!');
	},
	getTenant({ commit }) {
		return localStorage.getItem('tenant');
	},
	register({ state, commit, rootGetters, dispatch }, { user, tenant }) {
		return api.register(user, tenant).catch((err) => {
			const message = err.response.data.error.message;
			commit('auth_error', { message });
		});
	},
	requestPasswordReset({ state, commit, rootGetters, dispatch }, { email, tenant }) {
		return api
			.requestPasswordReset(email, tenant)
			.then((response) => {
				const message = response.data.message;
				commit('auth_successRequest', { message });
			})
			.catch((err) => {
				const message = err.response.data.message;
				commit('auth_error', { message });
			});
	},

	async validateToken({ commit }) {
		try {
			const response = await api.validateToken();
			const { tenant, token, user } = response.data;
			const storedTenant = localStorage.getItem('tenant');
			const isTenantChanged = storedTenant !== tenant;

			commit('set_token', { token });
			commit('auth_success', { user });

			if (!storedTenant || isTenantChanged) {
				localStorage.setItem('tenant', tenant);
			}

			try {
				const socket = await socketService.connect({
					tenant,
					token,
					component: 'web',
					forceReconnect: isTenantChanged,
				});

				if (socket) {
					installVueSocketIo(socket, this);
					useSocketStore().bindSocket(socket);
				}
			} catch (socketErr) {
				console.error('Socket connection error (Auth kept intact):', socketErr);
			}

			return true;
		} catch (err) {
			const currentPath = router.currentRoute.value?.path || router.currentRoute.path;
			if (currentPath !== '/login') {
				commit('logout');
				const message = err?.response?.data?.message || err?.message;
				if (message) {
					commit('auth_error', { message });
				}
			}

			return false;
		}
	},
	validateResetToken({ state, commit, rootGetters, dispatch }, token) {
		return api
			.validateResetToken(token)
			.then((response) => {
				if (response.status == 200) {
					commit('resetToken');
				}
			})
			.catch((err) => {
				console.log(err);
				commit('noResetToken');
			});
	},
	resetPassword({ state, commit, rootGetters, dispatch }, { password, token }) {
		return api
			.resetPassword(password, token)
			.then((res) => {
				const message = res.data.message;
				commit('auth_successReset', { message });
			})
			.catch((err) => {
				const message = err.response.data.message;
				commit('auth_error', { message });
			});
	},
	changePassword({ state, commit, rootGetters, dispatch }, { password }) {
		return api
			.changePassword(password)
			.then((res) => {
				const message = res.data.message;
				commit('auth_successReset', { message });
			})
			.catch((err) => {
				const message = err.response.data.message;
				commit('auth_error', { message });
			});
	},
	load({ state, commit, dispatch, rootState }) {
		return api
			.load()
			.then(async (response) => {
				const user = response.data.user;

				metricsService.initStoreWatcher(this);
				metricsService.initSession(getUserInfo(user), getDeviceInfo()).catch((err) => console.error('Metrics session failed', err));

				if (user.showProductionBanner !== undefined) {
					commit('profile/setShowProductionBanner', user.showProductionBanner, { root: true });
				}
				await dispatch('validateToken');

				commit('auth_success', { user });
				//Get notifications after login
				dispatch('notification/getNotifications', null, { root: true });

				//Get plugins after login
				dispatch('plugin/load', null, { root: true });
			})
			.catch((err) => {
				dispatch('logout');
				const message = err.response.data.message;
				commit('auth_error', { message });
			});
	},
	async loadTenants({ state, commit, dispatch, rootGetters }) {
		const response = await api.loadTenants();
		commit('set_tenants', { tenants: response.data });
	},
	async loadSignupAllowed({ state, commit }) {
		const response = await api.loadSignupAllowed(state.tenants);
		commit('set_signupAllowed', response.data);
	},
	setSelectedTenant({ commit }, tenant) {
		commit('set_selectedTenant', tenant);
	},
	removeError({ state, commit, rootGetters }) {
		commit('auth_removeErrorMessage');
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
